import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Check, X, Loader2, Building2, ArrowRight, Package, BookOpen, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

import { API } from '@/config';

const PricingPage = () => {
  const [data, setData] = useState({ tiers: [], features: [] });
  const [bundles, setBundles] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Determine if current user is a subscriber (org with active subscription)
  const isSubscriber = !!user?.organisation_id;

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/subscription-tiers/pricing`).then(res => setData(res.data)).catch(() => {}),
      axios.get(`${API}/bundles`).then(res => setBundles(res.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const tiers = data.tiers.filter(t => !t.is_custom);
  const customTier = data.tiers.find(t => t.is_custom);
  const allFeatures = data.features.map(f => f.name);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <Header />
        <div className="flex-1 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="pricing-page">
      <Helmet>
        <title>Pricing | Common Impact E-Learning</title>
        <meta name="description" content="Simple, transparent pricing for care sector e-learning. Plans for organisations of all sizes, from small care homes to large councils. Start training your team today." />
        <meta property="og:title" content="Pricing | Common Impact E-Learning" />
        <meta property="og:description" content="Flexible subscription plans for care sector organisations. All plans include core training modules." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2a3e5d] to-[#1e293b] py-16 text-center text-white">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 text-white">
          Simple, Transparent Pricing
        </h1>
        <p className="text-base lg:text-lg text-white/70 max-w-2xl mx-auto mb-8 px-4">
          Choose the plan that fits your care organisation. All plans include core training modules.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Label className="text-white/80 text-sm">Monthly</Label>
          <Switch
            checked={yearly}
            onCheckedChange={setYearly}
            data-testid="billing-toggle"
          />
          <Label className="text-white/80 text-sm">
            Annual <Badge className="bg-[#02d1d4] text-white ml-1 text-[10px]">Save 17%</Badge>
          </Label>
        </div>
      </section>

      {/* Tier Cards */}
      <section className="py-16 px-4 max-w-7xl mx-auto w-full">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {tiers.map((tier, i) => {
            const popular = i === 1;
            return (
              <Card
                key={tier.id}
                className={`relative border-2 transition-shadow hover:shadow-lg ${popular ? 'border-[#20acf6] shadow-md' : 'border-slate-200'}`}
                data-testid={`pricing-tier-${tier.id}`}
              >
                {popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#20acf6] text-white px-3 py-0.5 text-xs">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-lg font-semibold text-slate-900">{tier.name}</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">{tier.description}</p>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <div>
                    <span className="text-4xl font-bold text-[#2a3e5d]">
                      £{yearly ? Math.round(tier.price_yearly / 12) : tier.price_monthly}
                    </span>
                    <span className="text-slate-400 text-sm">/mo</span>
                    {yearly && (
                      <p className="text-xs text-[#02d1d4] mt-1">
                        £{tier.price_yearly}/year (billed annually)
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">
                    Up to <strong>{tier.user_limit}</strong> staff members
                  </p>
                  <Button
                    asChild
                    className={`w-full ${popular ? 'bg-[#20acf6] hover:bg-[#3bb8f7]' : 'bg-slate-800 hover:bg-slate-900'} text-white`}
                  >
                    <Link to="/register?type=org" data-testid={`pricing-cta-${tier.id}`}>
                      Get Started <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                  <div className="text-left pt-4 border-t border-slate-100 space-y-2.5">
                    {/* Show included features first in the tier's custom order, then excluded */}
                    {[
                      ...(tier.included_features || []),
                      ...allFeatures.filter(f => !(tier.included_features || []).includes(f))
                    ].map(feat => {
                      const included = tier.included_features?.includes(feat);
                      return (
                        <div key={feat} className="flex items-start gap-2 text-sm" data-testid={`feat-${tier.id}-${feat.replace(/\s/g, '-')}`}>
                          {included ? (
                            <Check className="h-4 w-4 text-[#02d1d4] shrink-0 mt-0.5" />
                          ) : (
                            <X className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />
                          )}
                          <span className={included ? 'text-slate-700' : 'text-slate-400'}>{feat}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Custom / Enterprise */}
          {customTier && (
            <Card className="border-2 border-dashed border-slate-300" data-testid="pricing-tier-custom">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg font-semibold text-slate-900">{customTier.name}</CardTitle>
                <p className="text-xs text-slate-500 mt-1">{customTier.description}</p>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div>
                  <span className="text-4xl font-bold text-[#20acf6]">Custom</span>
                </div>
                <p className="text-sm text-slate-500">For 500+ staff members</p>
                <Button asChild variant="outline" className="w-full border-[#20acf6] text-[#20acf6] hover:bg-[#20acf6]/5">
                  <Link to="/contact" data-testid="pricing-cta-custom">
                    <Building2 className="h-4 w-4 mr-2" /> Contact Sales
                  </Link>
                </Button>
                <div className="text-left pt-4 border-t border-slate-100 space-y-2.5">
                  {allFeatures.map(feat => (
                    <div key={feat} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-[#02d1d4] shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feat}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Course Bundles */}
      {bundles.length > 0 && (
        <section className="py-16 px-4 bg-white border-t border-slate-100" data-testid="bundles-section">
          <div className="max-w-7xl mx-auto w-full">
            <div className="text-center mb-10">
              <Badge className="bg-[#02d1d4]/15 text-[#02d1d4] border-[#02d1d4]/30 mb-3 text-xs">
                <Package className="h-3 w-3 mr-1" /> ONE-TIME PURCHASE
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#2a3e5d] mb-2">Course Bundles</h2>
              <p className="text-sm text-slate-500 max-w-xl mx-auto">
                Save with themed course bundles. Available to everyone — subscribers get an additional discount.
              </p>
            </div>

            {/* Group bundles by category */}
            {(() => {
              const categorised = {};
              const uncategorised = [];
              bundles.forEach(b => {
                if (b.category) {
                  if (!categorised[b.category]) categorised[b.category] = [];
                  categorised[b.category].push(b);
                } else {
                  uncategorised.push(b);
                }
              });
              const categoryNames = Object.keys(categorised).sort();

              const renderBundleCard = (bundle) => {
                const displayPrice = isSubscriber ? bundle.subscriber_price : bundle.full_price;
                const savings = bundle.full_price - bundle.subscriber_price;
                return (
                  <Card key={bundle.id} className="border-2 border-slate-200 hover:border-[#02d1d4]/40 transition-all hover:shadow-lg" data-testid={`bundle-${bundle.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg font-semibold text-[#2a3e5d] leading-snug">{bundle.title}</CardTitle>
                          <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{bundle.description}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-[#F8FAFC] rounded-xl p-4">
                        {isSubscriber ? (
                          <div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-3xl font-bold text-[#2a3e5d]">£{bundle.subscriber_price}</span>
                              <span className="text-sm text-slate-400 line-through">£{bundle.full_price}</span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <Tag className="h-3.5 w-3.5 text-[#02d1d4]" />
                              <span className="text-xs font-medium text-[#02d1d4]">You save £{savings.toFixed(0)} as a subscriber</span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-3xl font-bold text-[#2a3e5d]">£{bundle.full_price}</span>
                              <span className="text-xs text-slate-400">one-time</span>
                            </div>
                            {bundle.subscriber_price < bundle.full_price && (
                              <p className="text-xs text-[#02d1d4] mt-1 flex items-center gap-1">
                                <Tag className="h-3 w-3" />
                                {bundle.savings_text || `Subscribers pay £${bundle.subscriber_price}`}
                              </p>
                            )}
                          </div>
                        )}
                        {bundle.individual_total > bundle.full_price && (
                          <p className="text-[10px] text-slate-400 mt-2">Worth £{bundle.individual_total} if purchased individually</p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                          <BookOpen className="h-3.5 w-3.5" /> {bundle.course_count} course{bundle.course_count !== 1 ? 's' : ''} included
                        </p>
                        {(bundle.courses || []).slice(0, 5).map(c => (
                          <div key={c.id} className="flex items-center gap-2 text-sm">
                            <Check className="h-3.5 w-3.5 text-[#02d1d4] shrink-0" />
                            <span className="text-slate-700 truncate">{c.title}</span>
                          </div>
                        ))}
                        {(bundle.courses || []).length > 5 && (
                          <p className="text-xs text-slate-400 pl-5">+ {bundle.courses.length - 5} more</p>
                        )}
                      </div>

                      <Button asChild className="w-full bg-[#2a3e5d] hover:bg-[#1e293b] text-white" data-testid={`buy-bundle-${bundle.id}`}>
                        <Link to={user ? `/bundles/${bundle.id}` : '/login?redirect=/pricing'}>
                          Get This Bundle <ArrowRight className="h-4 w-4 ml-1" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              };

              return (
                <div className="space-y-10">
                  {categoryNames.map(catName => (
                    <div key={catName}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-1 h-6 bg-[#02d1d4] rounded-full" />
                        <h3 className="text-lg font-bold text-[#2a3e5d]">{catName} Bundles</h3>
                        <Badge variant="outline" className="text-xs text-slate-500">{categorised[catName].length}</Badge>
                      </div>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categorised[catName].map(renderBundleCard)}
                      </div>
                    </div>
                  ))}
                  {uncategorised.length > 0 && (
                    <div>
                      {categoryNames.length > 0 && (
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-1 h-6 bg-slate-300 rounded-full" />
                          <h3 className="text-lg font-bold text-[#2a3e5d]">Other Bundles</h3>
                          <Badge variant="outline" className="text-xs text-slate-500">{uncategorised.length}</Badge>
                        </div>
                      )}
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {uncategorised.map(renderBundleCard)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {!isSubscriber && (
              <div className="mt-8 text-center bg-[#02d1d4]/5 rounded-xl p-5 border border-[#02d1d4]/20">
                <p className="text-sm text-[#2a3e5d] font-medium">
                  Subscribe to any plan above and unlock discounted bundle pricing
                </p>
                <p className="text-xs text-slate-500 mt-1">Organisation subscribers save up to 33% on all course bundles</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-[#2a3e5d] mb-2">Frequently Asked Questions</h2>
          <p className="text-sm text-slate-500 mb-8">Need help choosing? Contact us at any time.</p>
          <div className="grid sm:grid-cols-2 gap-6 text-left">
            {[
              { q: 'Can I change plans later?', a: 'Yes — upgrade or downgrade at any time. Changes are prorated.' },
              { q: 'Is there a free trial?', a: 'We offer free taster modules on select courses so you can try before you buy.' },
              { q: 'What payment methods do you accept?', a: 'We accept all major credit and debit cards via Stripe.' },
              { q: 'Do you offer discounts for charities?', a: 'Yes — registered charities receive 15% off. Contact us for details.' },
            ].map((item, i) => (
              <div key={i} className="space-y-1">
                <h3 className="font-medium text-slate-800 text-sm">{item.q}</h3>
                <p className="text-xs text-slate-500">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
